import './GlobalNetwork.css';

/*
  Real world map — simplified vector, equirectangular projection.
  Source: amCharts SVG world map (CC-BY 4.0, free to use with attribution).
  Coordinates use the standard lon/lat → x/y formula:
    x = (lon + 180) / 360
    y = (90 - lat) / 180
*/

// Hub locations (real cities)
const HUBS = [
  { name: 'usa',        lon: -74.0,  lat: 40.7  },  // New York
  { name: 'europe',     lon: 2.35,   lat: 48.85 },  // Paris
  { name: 'uae',        lon: 55.3,   lat: 25.3  },  // Dubai
  { name: 'china',      lon: 116.4,  lat: 39.9  },  // Beijing
  { name: 'singapore',  lon: 103.8,  lat: 1.35  },  // Singapore
  { name: 'australia',  lon: 151.2,  lat: -33.9 },  // Sydney
];

const HQ = { lon: 77.2, lat: 28.6 };   // New Delhi

// Convert lon/lat → viewBox coords (0–360 wide, 0–180 tall)
const toXY = (lon, lat) => ({
  x: +(lon + 180).toFixed(2),
  y: +(90 - lat).toFixed(2),
});

const hq = toXY(HQ.lon, HQ.lat);

// Quadratic arc between two points, curved upward
const arcPath = (from, to) => {
  const mx = (from.x + to.x) / 2;
  const my = (from.y + to.y) / 2;
  const lift = Math.min(24, Math.abs(to.x - from.x) * 0.22);
  return `M ${from.x} ${from.y} Q ${mx} ${my - lift} ${to.x} ${to.y}`;
};

export default function GlobalNetwork() {
  return (
    <section className="global-network">
      <div className="gn-header">
        <span className="gn-eyebrow">Our Reach</span>
        <h2 className="gn-title">A Network That Spans the Globe</h2>
        <p className="gn-subtitle">
          Headquartered in New Delhi, MMOPL connects India to the world — from the Middle East
          and Europe to the Americas, Asia-Pacific and beyond — through trusted carriers,
          agents and logistics partners across continents.
        </p>
      </div>

      <div className="gn-map-wrapper">
        <svg
          className="gn-map-svg"
          viewBox="0 0 360 180"
          preserveAspectRatio="xMidYMid meet"
          role="img"
          aria-label="MMOPL global network centered on New Delhi"
        >
          <defs>
            <linearGradient id="gnArcGrad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#12d3a1" stopOpacity="0.95" />
              <stop offset="100%" stopColor="#1e88c7" stopOpacity="0.25" />
            </linearGradient>
            <radialGradient id="gnHqGlow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#12d3a1" stopOpacity="0.55" />
              <stop offset="100%" stopColor="#12d3a1" stopOpacity="0" />
            </radialGradient>
          </defs>

          {/* ===== REAL WORLD MAP — public-domain SVG ===== */}
          <image
            href="https://upload.wikimedia.org/wikipedia/commons/e/ec/World_map_blank_without_borders.svg"
            x="0"
            y="0"
            width="360"
            height="180"
            preserveAspectRatio="none"
            style={{
              filter: 'invert(1) brightness(1.4) contrast(1.2) sepia(0.6) hue-rotate(180deg) saturate(2.5)',
              opacity: 0.85,
            }}
          />

          {/* ===== Connection arcs from New Delhi ===== */}
          <g>
            {HUBS.map(h => {
              const p = toXY(h.lon, h.lat);
              return (
                <path
                  key={`arc-${h.name}`}
                  className="gn-arc"
                  d={arcPath(hq, p)}
                />
              );
            })}
          </g>

          {/* ===== Remote hub halos + dots ===== */}
          <g>
            {HUBS.map(h => {
              const p = toXY(h.lon, h.lat);
              return (
                <g key={`hub-${h.name}`}>
                  <circle
                    className="gn-hub-halo"
                    cx={p.x}
                    cy={p.y}
                    r={4}
                    style={{ transformOrigin: `${p.x}px ${p.y}px` }}
                  />
                  <circle className="gn-hub-dot" cx={p.x} cy={p.y} r={2.2} />
                </g>
              );
            })}
          </g>

          {/* ===== New Delhi HQ ===== */}
          <g>
            <circle cx={hq.x} cy={hq.y} r={18} fill="url(#gnHqGlow)" />
            <circle
              className="gn-hub-halo hq"
              cx={hq.x}
              cy={hq.y}
              r={7}
              style={{ transformOrigin: `${hq.x}px ${hq.y}px` }}
            />
            <circle
              className="gn-hub-halo hq"
              cx={hq.x}
              cy={hq.y}
              r={7}
              style={{ transformOrigin: `${hq.x}px ${hq.y}px`, animationDelay: '1.6s' }}
            />
            <circle className="gn-hub-dot hq" cx={hq.x} cy={hq.y} r={3.6} />
            <text className="gn-hq-label" x={hq.x} y={hq.y + 14}>
              NEW DELHI
            </text>
            <text className="gn-hq-sublabel" x={hq.x} y={hq.y + 20}>
              HEADQUARTERS
            </text>
          </g>
        </svg>
      </div>

      <div className="gn-stats">
        <div className="gn-stat">
          <div className="gn-stat__value">7+</div>
          <div className="gn-stat__label">Global Regions</div>
        </div>
        <div className="gn-stat">
          <div className="gn-stat__value">100+</div>
          <div className="gn-stat__label">Partner Ports</div>
        </div>
        <div className="gn-stat">
          <div className="gn-stat__value">24/7</div>
          <div className="gn-stat__label">Shipment Tracking</div>
        </div>
        <div className="gn-stat">
          <div className="gn-stat__value">360°</div>
          <div className="gn-stat__label">Logistics Support</div>
        </div>
      </div>
    </section>
  );
}